import { ObjectId, View } from "@novorender/api";
import { ReadonlyVec3, vec3 } from "gl-matrix";
import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";

import {
    clippingOutlineLaserActions,
    LaserLockedRotation,
    OutlineLaser,
    selectLockedRotation,
    selectOrthoAxisAligned,
    selectOutlineLaser3d,
} from "../outlineLaserSlice";
import { intersectionToLaserPoint, LASER_POS_Z_OFFSET } from "../utils";

export function useAddLaser() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const laser3d = useAppSelector(selectOutlineLaser3d);
    const lockedRotation = useAppSelector(selectLockedRotation);
    const orthoAxisAligned = useAppSelector(selectOrthoAxisAligned);
    const dispatch = useAppDispatch();
    const dispatchLastPickSample = useDispatchLastPickSample();

    return useCallback(
        (
            position: ReadonlyVec3,
            normal: ReadonlyVec3,
            {
                laserId,
                replaceLaserId,
                objectId,
                attachedToPlane,
                measureX = true,
                measureY = true,
                measureZ = true,
            }: {
                laserId: string;
                replaceLaserId?: string;
                objectId?: ObjectId;
                attachedToPlane?: boolean;
                measureX?: boolean;
                measureY?: boolean;
                measureZ?: boolean;
            },
        ) => {
            if (!view) {
                return false;
            }

            const laser = buildThreePointLaser({
                view,
                normal,
                objectId,
                position,
                lockedRotation: lockedRotation ?? undefined,
                measureX,
                measureY,
                measureZ: measureZ && laser3d,
                attachedToPlane: attachedToPlane ?? false,
                axisAligned: orthoAxisAligned,
            });

            if (!laser) {
                if (replaceLaserId) {
                    dispatch(clippingOutlineLaserActions.removeLaser(replaceLaserId));
                }
                return false;
            }

            laser.id = laserId;

            if (replaceLaserId) {
                dispatch(clippingOutlineLaserActions.replaceLaser({ laser, replaceId: replaceLaserId }));
            } else {
                dispatch(clippingOutlineLaserActions.addLaser(laser));
            }

            dispatchLastPickSample(lastPickSampleActions.set(null));
            return true;
        },
        [view, laser3d, dispatch, dispatchLastPickSample, lockedRotation, orthoAxisAligned],
    );
}

function buildThreePointLaser({
    view,
    normal,
    objectId,
    position,
    lockedRotation,
    measureX,
    measureY,
    measureZ,
    attachedToPlane,
    axisAligned,
}: {
    view: View;
    normal: ReadonlyVec3;
    objectId: ObjectId | undefined;
    position: ReadonlyVec3;
    lockedRotation?: LaserLockedRotation;
    measureX: boolean;
    measureY: boolean;
    measureZ: boolean;
    attachedToPlane: boolean;
    axisAligned?: boolean;
}) {
    if (!view.measure) {
        return;
    }

    let xDir: ReadonlyVec3 | undefined;
    if (axisAligned) {
        // If the normal is nearly vertical (close to [0, 0, 1]), we choose [1, 0, 0] as the x direction.
        // This is because any vector in the XY plane is perpendicular to the normal, and [1, 0, 0] is a simple, canonical choice.
        if (Math.abs(vec3.dot(normal, [0, 0, 1])) > 0.99) {
            xDir = vec3.fromValues(1, 0, 0);
        } else {
            // For other normals, we construct a vector perpendicular to the normal in the XY plane: [-normal[1], normal[0], 0].
            // This ensures xDir lies in the plane and is perpendicular to the normal, providing a consistent axis-aligned direction.
            xDir = vec3.fromValues(-normal[1], normal[0], 0);
            vec3.normalize(xDir, xDir);
        }
    } else if (lockedRotation && lockedRotation.intersection.left) {
        xDir = vec3.sub(vec3.create(), lockedRotation.intersection.left, lockedRotation.pos);
        vec3.normalize(xDir, xDir);
    }

    // Put laser 1mm below the clicked position, so it hits the walls of the clicked sufrace
    // if there are now walls around it.
    // E.g. you click on a roof represented by a cuboid - there are no walls around that roof,
    // so regular laser would just fly off the roof and vanish in the void.
    // But most of the time people want to measure the roof, so putting it slightly below the surface
    // makes it hit the walls of that cuboid roof.
    // That's not how real laser works, but that's what people expect.
    // At the same time imagine a room and we click on the floor, which is just a plane.
    // Now we want to hit the actual walls of the room (the way real laser would do).
    // For this we use `xyOffsets: [posIffset * 2]` - so 1mm both below and above the clicked position.
    const zOffset = attachedToPlane ? 0 : LASER_POS_Z_OFFSET;
    const shiftedPos = zOffset ? vec3.scaleAndAdd(vec3.create(), position, normal, zOffset) : position;
    // Why we need 2 results here?
    // First result - to show it.
    // Second result - to find out if there are more intersections and if we should show an arrow on the line
    // to find the next intersection.
    const resultLimit = 2;

    const intersections = view.threePointLaser({
        origin: shiftedPos,
        zDir: normal,
        xDir,
        left: measureX,
        right: measureX,
        up: measureY,
        down: measureY,
        zUp: measureZ,
        // This one should be higher than zOffset, because otherwise zUp
        // will hit the surface itself. It can be solved another way, but this is simpler.
        // And having some space around the origin is not that bad.
        minDistFromOrigin: Math.abs(zOffset) + 0.001,
        resultLimit,
        validateNormal: objectId && !attachedToPlane ? { objectId, position: position } : false,
        xyOffsets: zOffset ? [-zOffset * 2] : [],
    });
    if (!intersections) {
        return;
    }

    if (
        !(
            (intersections.left.length && intersections.right.length) ||
            (intersections.up.length && intersections.down.length) ||
            intersections.zUp.length
        )
    ) {
        // No use of adding a laser that hits nothing.
        return;
    }

    const result: OutlineLaser = {
        id: "",
        laserPosition: shiftedPos,
        laserObjectId: objectId,
        xDir: intersections.xDir,
        zDir: intersections.zDir,
        left: [],
        right: [],
        up: [],
        down: [],
        zUp: [],
        zDown: [
            {
                position: position,
            },
        ],
        hasMoreIntersections: {
            left: false,
            right: false,
            up: false,
            down: false,
            zUp: false,
            zDown: false,
        },
        attachedToPlane,
    };

    for (const key of ["left", "right", "up", "down", "zUp"] as const) {
        const list = intersections[key];

        result[key] = list.map(intersectionToLaserPoint);
        result.hasMoreIntersections[key] = list.length >= resultLimit;
    }

    result.measurementX = result.left.length && result.right.length ? { startIdx: 0, endIdx: 0 } : undefined;
    result.measurementY = result.up.length && result.down.length ? { startIdx: 0, endIdx: 0 } : undefined;
    result.measurementZ = result.zUp.length ? { startIdx: 0, endIdx: 0 } : undefined;

    return result;
}
